import { NotImplementedError } from "@greeter/error";
import { fromPromise, Subscription } from "xstate";
import * as z from "zod";

export class NotProvidedError extends Error {
  constructor() {
    super("Not provided");
  }
}

export type PromiseFn = (...args: any) => Promise<any>;

/**
 * This works as a nice complement to {@see notImplemented} as it adapts any promise function
 * into a fromPromise function.
 */
export function adapt<T extends PromiseFn>(f: T) {
  return fromPromise(
    async (args: { input: Parameters<T> }): Promise<ReturnType<T>> =>
      (await f(...args.input)) as ReturnType<T>
  );
}

/**
 * This is intended to function much like zod does for types but for promise actors for xstate.
 *
 * Use case is to create an interface for the StateMachine that can be provided at a later point.
 */
export function notImplemented<T extends PromiseFn>() {
  return fromPromise((_args: { input: Parameters<T> }): ReturnType<T> => {
    throw new NotImplementedError();
  });
}

export function notProvidedWithArgs<TInput, TOutput>(): (t: TInput) => TOutput {
  return function (): TOutput {
    throw new NotProvidedError();
  };
}

export function notProvidedNoArgs<TOutput>(): () => TOutput {
  return function (): TOutput {
    throw new NotProvidedError();
  };
}

export function PersistedSnapshotSchema<T extends z.ZodTypeAny>(
  contextSchema: T
) {
  return z.object({
    children: z.any(),
    context: contextSchema,
    historyValue: z.any(),
    status: z.string(),
    value: z.any(),
  });
}

export function unsubscriber(subscriptions: Array<Subscription>) {
  return function () {
    for (const subscription of subscriptions) {
      subscription.unsubscribe();
    }
  };
}

export * from "./util";
export * from "./hooks";
